import React, { useMemo, useRef, useState } from "react";
import { ProfileRow } from "../ProfileRow";
import { useTranslation } from "@helpers/translate";
import ArrowDownIcon from "@assets/icons/ArrowDownIcon";
import { buildTimezoneOptions } from "@helpers/timezone";
import SearchableSelect from "../SearchableSelect";

import "./TimezoneRow.sass";

const DEFAULT_OFFSET = "GMT+0";
const ARROW_ICON_SIZE = 16;
const TRIGGER_SELECTOR = ".timezone-select__trigger";

const getTimezoneOffset = (timezoneName) => {
  try {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: timezoneName,
      timeZoneName: "shortOffset",
    }).formatToParts(new Date());

    const rawOffset =
      parts.find((part) => part.type === "timeZoneName")?.value || DEFAULT_OFFSET;

    return rawOffset.replace("UTC", "GMT");
  } catch (error) {
    return DEFAULT_OFFSET;
  }
};

const formatTimezoneLabel = (timezoneName) => {
  if (!timezoneName) {
    return "";
  }

  return `${timezoneName} (${getTimezoneOffset(timezoneName)})`;
};

const renderTimezoneOption = (option) => (
  <>
    <span className="timezone-select__name">{option.name}</span>
    <span className="timezone-select__offset">{option.offset}</span>
  </>
);

export const TimezoneRow = ({ timezone, onSelectTimezone }) => {
  const { __ } = useTranslation();
  const selectRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);

  const options = useMemo(
    () =>
      buildTimezoneOptions(timezone).map((option) => ({ ...option, key: option.timezone, value: option.timezone })),
    [timezone],
  );

  return (
    <div className="timezone-row">
      <ProfileRow
        label={__("profile.timezone")}
        value={
          <div className="profile-row__edit-input profile-row__timezone-input">
            <SearchableSelect
              ref={selectRef}
              options={options}
              value={timezone}
              onChange={onSelectTimezone}
              onOpenChange={setIsOpen}
              triggerSelector={TRIGGER_SELECTOR}
              className="timezone-select"
              controlClassName="timezone-select__control"
              renderValue={() => (
                <span className="timezone-select__value">{formatTimezoneLabel(timezone)}</span>
              )}
              renderOption={renderTimezoneOption}
            />
          </div>
        }
        actions={
          <button
            type="button"
            className="profile-action__button timezone-select__trigger"
            onClick={() => selectRef.current?.toggle()}
            aria-label={__("profile.timezone")}
            aria-expanded={isOpen}
          >
            <span
              className={`timezone-select__arrow ${isOpen ? "timezone-select__arrow--open" : ""}`}
              aria-hidden="true"
            >
              <ArrowDownIcon size={ARROW_ICON_SIZE} />
            </span>
          </button>
        }
      />
    </div>
  );
};

export default TimezoneRow;
